/**
 * The Alert_Rule create/edit form (Feature: granular-crud-api, task 13.2; Requirements 11.4, 11.5,
 * 11.6; Design: §11).
 *
 * Structured exactly like {@link import('./CheckForm').CheckForm} — one component for both writes,
 * raw text held for numeric and list inputs and parsed at submit so the API stays the single
 * validator, and no ownership of the caller's data. The rule-specific surface is the four decisions
 * an Alert_Rule encodes:
 *
 *  - **which Checks it applies to** — the `checkSelector`, whose `kind` chooses whether the rule
 *    covers the whole Project (`all`), named Checks (`logicalName`), or specific ids (`checkId`); the
 *    dependent list input appears only for the kinds that take one;
 *  - **when it fires** — `fireOn` plus the optional `consecutiveFailures` threshold;
 *  - **how loud it is** — `severity` and the `maxNotificationRate` window that caps alert storms;
 *  - **where it goes** — `channels` (Notification_Channel ids) and `integrations` (the paging
 *    platforms), the latter as checkboxes since the set is closed and small.
 *
 * `maxNotificationRate` is one API field (`{ maxNotifications, windowSeconds }`) but two inputs, so
 * both inputs share the `alertRule.maxNotificationRate` error path — a defect in either lands on the
 * pair, which is where the API reports it.
 */

import { useState, type FormEvent, type ReactNode } from 'react';

import type { FieldError, FireOn, IntegrationType, Severity } from '@soroush/core';

import type {
  AlertRuleDto,
  AlertRulePatch,
  CheckSelectorDto,
  CreateAlertRulePayload,
} from '../api/types';

import { FormErrorPanel, FormField, unmappedFieldErrors } from './FieldErrorText';

const SELECTOR_KINDS = ['all', 'logicalName', 'checkId'] as const;
type SelectorKind = (typeof SELECTOR_KINDS)[number];

const SELECTOR_LABELS: Record<SelectorKind, string> = {
  all: 'All checks in the project',
  logicalName: 'Checks by name',
  checkId: 'Checks by id',
};

const FIRE_ON: readonly FireOn[] = ['down', 'degraded'];
const SEVERITIES: readonly Severity[] = ['critical', 'high', 'medium', 'low', 'info'];
const INTEGRATIONS: readonly IntegrationType[] = [
  'pagerduty',
  'opsgenie',
  'firehydrant',
  'incidentio',
  'rootly',
  'statuspage',
];

/* -------------------------------------------------------------------------- */
/* Draft state                                                                 */
/* -------------------------------------------------------------------------- */

interface RuleDraft {
  projectId: string;
  selectorKind: SelectorKind;
  /** Comma-separated names or ids, per the selector kind. */
  selectorValues: string;
  fireOn: FireOn;
  severity: Severity;
  maxNotifications: string;
  windowSeconds: string;
  consecutiveFailures: string;
  groupingKey: string;
  channels: string;
  integrations: IntegrationType[];
}

function text(value: unknown): string {
  return value === undefined || value === null ? '' : String(value);
}

function blankDraft(): RuleDraft {
  return {
    projectId: '',
    selectorKind: 'all',
    selectorValues: '',
    fireOn: 'down',
    severity: 'high',
    maxNotifications: '3',
    windowSeconds: '3600',
    consecutiveFailures: '',
    groupingKey: '',
    channels: '',
    integrations: [],
  };
}

function draftFromRule(rule: AlertRuleDto): RuleDraft {
  const selector = rule.checkSelector;
  const kind: SelectorKind = SELECTOR_KINDS.includes(selector?.kind as SelectorKind)
    ? (selector.kind as SelectorKind)
    : 'all';
  const values =
    kind === 'logicalName'
      ? ((selector as { names?: string[] }).names ?? [])
      : kind === 'checkId'
        ? ((selector as { checkIds?: string[] }).checkIds ?? [])
        : [];
  const rate = rule.maxNotificationRate;
  const blank = blankDraft();
  return {
    projectId: text(rule.projectId),
    selectorKind: kind,
    selectorValues: values.map(text).join(', '),
    fireOn: FIRE_ON.includes(rule.fireOn) ? rule.fireOn : 'down',
    severity: SEVERITIES.includes(rule.severity) ? rule.severity : 'high',
    maxNotifications: rate === undefined ? blank.maxNotifications : text(rate.maxNotifications),
    windowSeconds: rate === undefined ? blank.windowSeconds : text(rate.windowSeconds),
    consecutiveFailures: text(rule.consecutiveFailures),
    groupingKey: text(rule.groupingKey),
    channels: Array.isArray(rule.channels) ? rule.channels.map(text).join(', ') : '',
    integrations: Array.isArray(rule.integrations) ? [...rule.integrations] : [],
  };
}

/* -------------------------------------------------------------------------- */
/* Draft → wire body                                                           */
/* -------------------------------------------------------------------------- */

/** See `CheckForm`: an unparseable number goes to the API verbatim so it reports the field. */
function numeric(raw: string): number {
  const trimmed = raw.trim();
  const parsed = Number(trimmed);
  const value = trimmed.length > 0 && Number.isFinite(parsed) ? parsed : trimmed;
  return value as number;
}

function list(raw: string): string[] {
  return raw
    .split(',')
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
}

function selectorFromDraft(draft: RuleDraft): CheckSelectorDto {
  const values = list(draft.selectorValues);
  if (draft.selectorKind === 'logicalName') return { kind: 'logicalName', names: values };
  if (draft.selectorKind === 'checkId') return { kind: 'checkId', checkIds: values };
  return { kind: 'all' };
}

function mutableFields(draft: RuleDraft): Omit<CreateAlertRulePayload, 'projectId'> {
  return {
    checkSelector: selectorFromDraft(draft),
    fireOn: draft.fireOn,
    severity: draft.severity,
    maxNotificationRate: {
      maxNotifications: numeric(draft.maxNotifications),
      windowSeconds: numeric(draft.windowSeconds),
    },
    channels: list(draft.channels),
    integrations: [...draft.integrations],
  };
}

/** The `POST /v1/alert-rules` body; blank optional fields are omitted rather than sent empty. */
export function createPayloadFromDraft(draft: RuleDraft): CreateAlertRulePayload {
  return {
    projectId: draft.projectId.trim(),
    ...mutableFields(draft),
    ...(draft.consecutiveFailures.trim().length > 0
      ? { consecutiveFailures: numeric(draft.consecutiveFailures) }
      : {}),
    ...(draft.groupingKey.trim().length > 0 ? { groupingKey: draft.groupingKey.trim() } : {}),
  };
}

/**
 * The `PATCH /v1/alert-rules/{id}` body. A blank optional field is sent as an explicit `null`, which
 * is how a merge patch *clears* it (omitting it would leave the stored value); `projectId` is
 * excluded because the API rejects an immutable field in a patch body.
 */
export function patchFromDraft(draft: RuleDraft): AlertRulePatch {
  return {
    ...mutableFields(draft),
    consecutiveFailures:
      draft.consecutiveFailures.trim().length > 0 ? numeric(draft.consecutiveFailures) : null,
    groupingKey: draft.groupingKey.trim().length > 0 ? draft.groupingKey.trim() : null,
  };
}

/* -------------------------------------------------------------------------- */
/* Component                                                                   */
/* -------------------------------------------------------------------------- */

function mappedPaths(draft: RuleDraft): string[] {
  const paths = [
    'alertRule.projectId',
    'alertRule.checkSelector',
    'alertRule.checkSelector.kind',
    'alertRule.fireOn',
    'alertRule.severity',
    'alertRule.maxNotificationRate',
    'alertRule.consecutiveFailures',
    'alertRule.groupingKey',
    'alertRule.channels',
    'alertRule.integrations',
  ];
  if (draft.selectorKind === 'logicalName') paths.push('alertRule.checkSelector.names');
  if (draft.selectorKind === 'checkId') paths.push('alertRule.checkSelector.checkIds');
  return paths;
}

/** The error path of the dependent selector list, which differs per kind. */
function selectorValuesPath(kind: SelectorKind): string {
  return kind === 'checkId' ? 'alertRule.checkSelector.checkIds' : 'alertRule.checkSelector.names';
}

interface AlertRuleFormCommon {
  submitting?: boolean;
  fieldErrors?: readonly FieldError[];
  errorMessage?: string;
  /** Slot for the provenance notice on a manifest- or Terraform-managed rule (Req 11.8, 13.1). */
  notice?: ReactNode;
  /**
   * Hold the submit back for a reason the form cannot see (Feature: terraform-provider, task 9.2; Req
   * 13.3) — today, an unticked Terraform acknowledgement in {@link notice}. Unlike `readOnly` and
   * `submitting` it disables the submit alone, so the user can keep editing while they decide.
   */
  submitDisabled?: boolean;
  /** A viewer sees the values but cannot submit (Req 11.7). */
  readOnly?: boolean;
  onCancel?: () => void;
}

export type AlertRuleFormProps = AlertRuleFormCommon &
  (
    | { mode: 'create'; initial?: undefined; onSubmit: (payload: CreateAlertRulePayload) => void }
    | { mode: 'edit'; initial: AlertRuleDto; onSubmit: (patch: AlertRulePatch) => void }
  );

export function AlertRuleForm(props: AlertRuleFormProps): JSX.Element {
  const {
    submitting = false,
    fieldErrors,
    errorMessage,
    notice,
    submitDisabled = false,
    readOnly = false,
  } = props;
  const [draft, setDraft] = useState<RuleDraft>(() =>
    props.mode === 'edit' ? draftFromRule(props.initial) : blankDraft(),
  );

  function update<K extends keyof RuleDraft>(key: K, value: RuleDraft[K]): void {
    setDraft((current) => ({ ...current, [key]: value }));
  }

  function toggleIntegration(integration: IntegrationType, on: boolean): void {
    setDraft((current) => ({
      ...current,
      integrations: on
        ? [...current.integrations, integration]
        : current.integrations.filter((entry) => entry !== integration),
    }));
  }

  const disabled = readOnly || submitting;

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    // `submitDisabled` is checked here as well as on the button: Enter in a text input submits a form
    // whose button is disabled, and Req 13.3 is about the request not being issued.
    if (disabled || submitDisabled) return;
    if (props.mode === 'edit') props.onSubmit(patchFromDraft(draft));
    else props.onSubmit(createPayloadFromDraft(draft));
  }

  return (
    <form className="entity-form" onSubmit={handleSubmit} noValidate>
      {notice}
      <FormErrorPanel
        message={errorMessage}
        unmapped={unmappedFieldErrors(fieldErrors, mappedPaths(draft))}
      />

      {props.mode === 'create' ? (
        <FormField
          label="Project"
          path="alertRule.projectId"
          errors={fieldErrors}
          render={(field) => (
            <input
              {...field}
              type="text"
              value={draft.projectId}
              disabled={disabled}
              onChange={(event) => update('projectId', event.target.value)}
            />
          )}
        />
      ) : null}

      <FormField
        label="Applies to"
        path="alertRule.checkSelector.kind"
        errors={fieldErrors}
        render={(field) => (
          <select
            {...field}
            value={draft.selectorKind}
            disabled={disabled}
            onChange={(event) => update('selectorKind', event.target.value as SelectorKind)}
          >
            {SELECTOR_KINDS.map((kind) => (
              <option key={kind} value={kind}>
                {SELECTOR_LABELS[kind]}
              </option>
            ))}
          </select>
        )}
      />

      {draft.selectorKind !== 'all' ? (
        <FormField
          label={draft.selectorKind === 'checkId' ? 'Check ids' : 'Check names'}
          path={selectorValuesPath(draft.selectorKind)}
          errors={fieldErrors}
          hint="Comma-separated."
          render={(field) => (
            <input
              {...field}
              type="text"
              value={draft.selectorValues}
              disabled={disabled}
              onChange={(event) => update('selectorValues', event.target.value)}
            />
          )}
        />
      ) : null}

      <FormField
        label="Fire on"
        path="alertRule.fireOn"
        errors={fieldErrors}
        render={(field) => (
          <select
            {...field}
            value={draft.fireOn}
            disabled={disabled}
            onChange={(event) => update('fireOn', event.target.value as FireOn)}
          >
            {FIRE_ON.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        )}
      />

      <FormField
        label="Severity"
        path="alertRule.severity"
        errors={fieldErrors}
        render={(field) => (
          <select
            {...field}
            value={draft.severity}
            disabled={disabled}
            onChange={(event) => update('severity', event.target.value as Severity)}
          >
            {SEVERITIES.map((value) => (
              <option key={value} value={value}>
                {value}
              </option>
            ))}
          </select>
        )}
      />

      <FormField
        label="Notification rate limit"
        path="alertRule.maxNotificationRate"
        errors={fieldErrors}
        hint="Maximum notifications per window (seconds)."
        render={(field) => (
          <span className="input-pair">
            <input
              {...field}
              type="number"
              aria-label="Max notifications"
              value={draft.maxNotifications}
              disabled={disabled}
              onChange={(event) => update('maxNotifications', event.target.value)}
            />
            <input
              type="number"
              aria-label="Rate window seconds"
              value={draft.windowSeconds}
              disabled={disabled}
              onChange={(event) => update('windowSeconds', event.target.value)}
            />
          </span>
        )}
      />

      <FormField
        label="Consecutive failures"
        path="alertRule.consecutiveFailures"
        errors={fieldErrors}
        hint="Leave blank to fire on the first qualifying result."
        render={(field) => (
          <input
            {...field}
            type="number"
            value={draft.consecutiveFailures}
            disabled={disabled}
            onChange={(event) => update('consecutiveFailures', event.target.value)}
          />
        )}
      />

      <FormField
        label="Grouping key"
        path="alertRule.groupingKey"
        errors={fieldErrors}
        hint="Alerts sharing a key collapse into one notification."
        render={(field) => (
          <input
            {...field}
            type="text"
            value={draft.groupingKey}
            disabled={disabled}
            onChange={(event) => update('groupingKey', event.target.value)}
          />
        )}
      />

      <FormField
        label="Channels"
        path="alertRule.channels"
        errors={fieldErrors}
        hint="Comma-separated notification channel ids."
        render={(field) => (
          <input
            {...field}
            type="text"
            value={draft.channels}
            disabled={disabled}
            onChange={(event) => update('channels', event.target.value)}
          />
        )}
      />

      <fieldset className="form-section">
        <legend>Integrations</legend>
        {INTEGRATIONS.map((integration) => (
          <label key={integration} className="checkbox-row">
            <input
              type="checkbox"
              checked={draft.integrations.includes(integration)}
              disabled={disabled}
              onChange={(event) => toggleIntegration(integration, event.target.checked)}
            />
            {integration}
          </label>
        ))}
      </fieldset>

      <div className="form-actions">
        <button type="submit" disabled={disabled || submitDisabled}>
          {props.mode === 'create' ? 'Create alert rule' : 'Save alert rule'}
        </button>
        {props.onCancel !== undefined ? (
          <button type="button" className="secondary" onClick={props.onCancel}>
            Cancel
          </button>
        ) : null}
      </div>
    </form>
  );
}
